// SPEC-17 · Perfil, a tab under the navigation pill since SPEC-27, with no
// ←. Her Dados gerais as she gave them, the way to edit them, the privacy
// promise, and the sign-out.
//
// The age is poweryourflow-lib's (ageOn), the place of Dados gerais on the
// hub too (hubPlacement); this screen words what the device holds. Signing
// out first tries to send what waits on the device, and says so when some
// of it would be lost (SPEC-17, the founder's answer of 2026-10-15).
//
// SPEC-31: the Idioma card after Dados gerais holds the same « PT · FR · EN »
// as Boas-vindas. Every label follows the language; her values do not.
//
// SPEC-55: Lateralidade sits between Posição and Nível, her answer worded in
// the language (an option's words, or what she typed), « — » on an account
// older than the question, which Editar asks.

import { useEffect, useRef, useState } from "react";
import { ageOn, answersFromProfile, hubPlacement } from "poweryourflow-lib";

import { Avatar } from "../components/Avatar";
import { LanguageControl } from "../components/LanguageControl";
import { NavPill } from "../components/NavPill";
import { useCopy, useLanguage, useLanguageFade } from "../i18n/LanguageProvider";
import { forgetPlaces } from "../lib/place";
import { paths, useRouter } from "../lib/router";
import { localToday, profileRows } from "../onboarding/conversation";
import { useLateralityWords } from "../profile/lateralityWords";
import type { ProfileSession } from "../profile/profile";
import { codeOf, useHub } from "./useHub";

/**
 * idle → sending (only while answers wait) → confirm ↔ leaving → Boas-vindas.
 * `unsent` adds the warning; `failed` says the last "Terminar" did not go through.
 */
type SignOutStep =
  | { step: "idle" }
  | { step: "sending" }
  | { step: "confirm"; unsent: boolean; failed: boolean }
  | { step: "leaving"; unsent: boolean };

const wideButton =
  "flex h-[var(--control-h)] w-full items-center justify-center rounded-pill text-body font-semibold " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf disabled:text-disabled";
const secondaryButton = `${wideButton} border border-line-20 text-dark hover:bg-cream-deep`;

function ConfirmSheet({
  unsent,
  failed,
  busy,
  onConfirm,
  onCancel,
}: {
  unsent: boolean;
  failed: boolean;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const text = useCopy().profile;
  const cancel = useRef<HTMLButtonElement>(null);

  // The safe answer has the focus, and Escape gives it.
  useEffect(() => {
    cancel.current?.focus();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);

  return (
    // Siblings, so the panel's fade is not multiplied by the backdrop's.
    <>
      <div className="backdrop-enter fixed inset-0 z-10 bg-line-25" onClick={busy ? undefined : onCancel} />
      <div className="fixed inset-x-0 bottom-0 z-10 flex justify-center">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="sign-out-title"
          className="screen-enter flex w-full max-w-[430px] flex-col gap-3 rounded-t-card bg-white px-5 pt-6 pb-8 shadow-float"
        >
          <h2 id="sign-out-title" className="text-title font-semibold tracking-[var(--ls-title)]">
            {text.confirmTitle}
          </h2>
          {unsent && <p className="text-body leading-[var(--lh-body)] text-muted">{text.unsent}</p>}
          {failed && (
            <p role="alert" className="text-body-sm text-orange-ink">
              {text.signOutFailed}
            </p>
          )}
          <button type="button" disabled={busy} onClick={onConfirm} className={`${wideButton} mt-2 bg-dark text-cream hover:bg-dark-hover`}>
            {busy ? text.leaving : text.confirm}
          </button>
          <button ref={cancel} type="button" disabled={busy} onClick={onCancel} className={secondaryButton}>
            {text.cancel}
          </button>
        </div>
      </div>
    </>
  );
}

/** SPEC-31 · « IDIOMA », its line, the control on the right; and the save that failed, under them. */
function LanguageCard() {
  const text = useCopy().profile.language;
  const { saveFailed } = useLanguage();
  return (
    <section aria-labelledby="language-title" className="flex flex-col rounded-card bg-white p-5">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-1">
          <h2 id="language-title" className="text-label font-semibold tracking-[var(--ls-label)] text-muted uppercase">
            {text.title}
          </h2>
          <p className="text-caption text-muted">{text.line}</p>
        </div>
        <LanguageControl label={text.title} />
      </div>
      {/* Always there, so a screen reader hears the failure when it lands. */}
      <div role="status">{saveFailed && <p className="pt-3 text-body-sm text-orange-ink">{text.saveFailed}</p>}</div>
    </section>
  );
}

export function Profile({ session }: { session: ProfileSession }) {
  const { navigate } = useRouter();
  const words = useCopy();
  const side = useLateralityWords();
  const text = words.profile;
  const fade = useLanguageFade();
  const { hub, failed, retry } = useHub(session, "perfil");
  const [signOut, setSignOut] = useState<SignOutStep>({ step: "idle" });

  /** Sends what waits on the device, then asks; the sheet warns only when something is still unsent. */
  const askToSignOut = async () => {
    if (!hub) return;
    setSignOut({ step: "sending" });
    let unsent = true;
    try {
      const data = session.data();
      const waiting = await data.pendingAnswers(hub.accountId);
      unsent = waiting > 0 && (await data.prepareLogout(hub.accountId)) > 0;
    } catch (error) {
      // Unable to tell: warn, since signing out would lose whatever waits.
      console.error("[perfil] could not count the unsent answers", { code: codeOf(error) });
    }
    setSignOut({ step: "confirm", unsent, failed: false });
  };

  /** Deletes the device's data, then closes the session: a failure before the end keeps her signed in, to try again. */
  const leave = async (unsent: boolean) => {
    setSignOut({ step: "leaving", unsent });
    try {
      await session.data().clearLocalData();
      await session.signOut();
      forgetPlaces();
    } catch (error) {
      console.error("[perfil] sign-out failed", { code: codeOf(error) });
      setSignOut({ step: "confirm", unsent, failed: true });
      return;
    }
    navigate(paths.welcome, { replace: true });
  };

  const dados = hub?.assessments.find((entry) => entry.assessmentId === "dados")?.run ?? null;
  const complete = hubPlacement("dados", dados).group === "done";
  const profile = hub?.profile ?? null;
  // Her stored profile first, as the edit offers it; the finished run's answers until the profile arrives.
  const answers = profile ? answersFromProfile(profile) : (dados?.answers ?? {});
  const today = localToday();
  const age = complete && profile?.birthDate ? ageOn(profile.birthDate, today) : undefined;
  const firstName = profile?.firstName ?? "";

  return (
    <>
      <main className="flex min-h-dvh flex-col bg-cream">
        <NavPill active="profile" firstName={firstName} />
        <h1 className="sr-only">{text.title}</h1>

        <div className={`${fade || "tab-enter"} flex flex-col gap-4 px-5 pt-6 pb-8`}>
          {failed && (
            <div className="flex flex-col gap-3">
              <p className="text-body leading-[var(--lh-body)] text-muted">{words.assessments.failed}</p>
              <button
                type="button"
                onClick={retry}
                className={`${wideButton} bg-dark text-cream hover:bg-dark-hover`}
              >
                {words.assessments.retry}
              </button>
            </div>
          )}

          {!failed && !hub && (
            <div role="status" aria-label={text.loading} className="flex flex-col items-center gap-4">
              <div className="size-20 rounded-pill bg-cream-deep" />
              <div className="h-72 w-full rounded-card bg-cream-deep" />
            </div>
          )}

          {!failed && hub && (
            <>
              <section className="flex flex-col items-center gap-2 pb-2 text-center">
                <Avatar firstName={firstName} size="large" />
                <p className="pt-2 text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)]">{firstName}</p>
                {age !== undefined && (
                  <p className="text-body-sm text-muted">
                    {words.assessments.profileLine(age, profile?.modality ?? "", profile?.position ?? "")}
                  </p>
                )}
              </section>

              <section aria-labelledby="general-data" className="rounded-card bg-white p-5">
                <div className="flex items-center justify-between">
                  <h2 id="general-data" className="text-label font-semibold tracking-[var(--ls-label)] text-muted uppercase">
                    {text.generalData}
                  </h2>
                  <button
                    type="button"
                    onClick={() => navigate(paths.profileEdit)}
                    className="flex min-h-[var(--touch-min)] items-center text-body-sm font-semibold text-violet hover:text-violet-ink"
                  >
                    {complete ? text.edit : text.answer}
                  </button>
                </div>
                {complete ? (
                  <dl className="flex flex-col">
                    {profileRows(answers, today, words, side).map((row) => (
                      <div key={row.label} className="flex justify-between gap-4 border-t border-line-8 py-3 first:border-t-0">
                        <dt className="text-body-sm text-muted">{row.label}</dt>
                        <dd className="min-w-0 text-right text-body-sm font-medium break-words">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="pt-2 text-body text-muted">{text.notAnswered}</p>
                )}
              </section>

              <LanguageCard />

              <section className="rounded-card bg-violet-tint p-5">
                <p className="text-body font-semibold text-violet-ink">{text.privacyTitle}</p>
                <p className="pt-1 text-body-sm leading-[var(--lh-body)] text-violet-ink">{text.privacyText}</p>
              </section>

              <button
                type="button"
                disabled={signOut.step !== "idle"}
                onClick={() => void askToSignOut()}
                className={`${secondaryButton} mt-2`}
              >
                {signOut.step === "sending" ? text.sending : text.signOut}
              </button>
            </>
          )}
        </div>
      </main>

      {/* Outside <main>: its entrance animates a transform, which would make it the box a fixed sheet is placed in. */}
      {(signOut.step === "confirm" || signOut.step === "leaving") && (
        <ConfirmSheet
          unsent={signOut.unsent}
          failed={signOut.step === "confirm" && signOut.failed}
          busy={signOut.step === "leaving"}
          onConfirm={() => void leave(signOut.unsent)}
          onCancel={() => setSignOut({ step: "idle" })}
        />
      )}
    </>
  );
}
